import { useState } from "react";
import { ArrowLeft, FileDown, Loader2, CheckCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useCreditPrompt } from "@/hooks/useCreditPrompt";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const Section = ({ title, children }) => (
  <div className="border-b-2 pb-4 mb-4" style={{ borderColor: 'var(--border-light)' }}>
    <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
    {children}
  </div>
);
const Field = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between py-1 text-sm">
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span className="font-semibold text-right max-w-[60%]">{String(value)}</span>
    </div>
  );
};

export default function ThreeCircuitStep3({ formData, onPrev, onGenerated }) {
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const { showModal, modalCreditType, promptBuyCredits, closeModal, buyCredits } = useCreditPrompt();

  const buildPayload = () => ({
    ...formData,
    cert_date: formData.cert_date ? format(formData.cert_date, "dd/MM/yyyy") : "",
  });

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await authFetch(`${API}/api/certificates/three-circuit-mw/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Preview failed");
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (err) { toast.error(err.message); }
    finally { setPreviewing(false); }
  };

  const handleGenerate = async () => {
    if (!navigator.onLine) {
      toast.error("You're offline — please save your draft and generate when back online.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/api/certificates/three-circuit-mw`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (res.status === 402) {
        promptBuyCredits("electrical");
        return;
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      const data = await res.json();
      setPdfUrl(`${API}/api/certificates/domestic-eic/${data.certificate_id}/download`);
      setCompleted(true);
      toast.success("Certificate generated!");
      if (onGenerated) onGenerated();
      // Save contractor
      authFetch(`${API}/api/user/contractor-details`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          contractor_trading_title: formData.contractor_trading_title,
          contractor_email: formData.contractor_email, contractor_phone: formData.contractor_phone,
          contractor_address: formData.contractor_address, contractor_postcode: formData.contractor_postcode,
          contractor_reg_no: formData.contractor_reg_no,
        }),
      }).catch(() => {});
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  if (completed) {
    return (
      <div className="form-container max-w-3xl mx-auto text-center" data-testid="tcmw-complete">
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Certificate Generated</h2>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Your Three Circuit Minor Works certificate has been created.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {pdfUrl && <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="tcmw-download-pdf" className="brutalist-btn flex items-center justify-center gap-2"><FileDown className="w-4 h-4" /> Download PDF</a>}
          <a href="/dashboard" className="brutalist-btn-outline flex items-center justify-center gap-2">Back to Dashboard</a>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="tcmw-step-3">
      <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Review & Generate</h2>
      <p className="text-xs mb-6" style={{ color: 'var(--text-secondary)' }}>Review details before generating the certificate.</p>

      <Section title="Works & Earthing">
        <Field label="Client" value={formData.client_name} />
        <Field label="Description" value={formData.description_of_works} />
        <Field label="Address" value={formData.installation_address} />
        <Field label="Postcode" value={formData.installation_postcode} />
        <Field label="System earthing" value={formData.system_earthing} />
        <Field label="Zdb (Ω)" value={formData.zdb} />
        <Field label="Date" value={formData.cert_date ? format(formData.cert_date, "dd/MM/yyyy") : ""} />
      </Section>

      <Section title="Circuit Test Results">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--border-main)', color: 'white' }}>
                <th className="p-1.5 text-left">Circuit</th>
                <th className="p-1.5">Description</th>
                <th className="p-1.5">OCPD</th>
                <th className="p-1.5">R1+R2</th>
                <th className="p-1.5">Zs</th>
                <th className="p-1.5">IR L-E</th>
                <th className="p-1.5">Polarity</th>
              </tr>
            </thead>
            <tbody>
              {(formData.circuits || []).map((c, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'var(--bg-card)' : 'var(--row-alt)' }}>
                  <td className="p-1.5 font-bold">{c.circuit_number || i + 1}</td>
                  <td className="p-1.5">{c.circuit_description}</td>
                  <td className="p-1.5 text-center">{c.ocpd_type} {c.ocpd_rating}</td>
                  <td className="p-1.5 text-center">{c.r1r2}</td>
                  <td className="p-1.5 text-center">{c.max_zs}</td>
                  <td className="p-1.5 text-center">{c.ir_live_earth}</td>
                  <td className="p-1.5 text-center">{c.polarity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Contractor">
        <Field label="Trading title" value={formData.contractor_trading_title} />
        <Field label="Email" value={formData.contractor_email} />
        <Field label="Electrician" value={formData.electrician_name} />
        <Field label="Reg No." value={formData.contractor_reg_no} />
      </Section>

      <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
        <button onClick={onPrev} className="brutalist-btn-outline flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Previous</button>
        <div className="flex gap-3">
          <button data-testid="tcmw-preview-btn" onClick={handlePreview} disabled={previewing} className="brutalist-btn-outline flex items-center gap-2">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview
          </button>
          <button data-testid="tcmw-generate-btn" onClick={handleGenerate} disabled={submitting} className="brutalist-btn flex items-center gap-2">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileDown className="w-4 h-4" /> Generate</>}
          </button>
        </div>
      </div>
      <BuyCreditsModal open={showModal} onClose={closeModal} creditType={modalCreditType} onBuy={buyCredits} />
    </div>
  );
}
